import {
  Button,
  closeCurrentDialog,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
} from 'platform/components';
import {Grid, GridItem, HStack, VStack} from 'platform/foundation';
import {useCurrencySymbolFormatter} from 'platform/locale';
import {Nullish} from 'utility-types';
import {z} from 'zod';

import {isNil, isNotNil} from 'ramda';

import {RentalPriceGroupItemResponseBody} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {getPriceWithVat, useTenant} from '@dms/shared';

import {PriceListItem} from '../types/PriceListItem';
import {RateFormRow} from './RateFormRow';

type DefaultValues =
  | (PriceListItem & {
      originalIndex: number;
    })
  | RentalPriceGroupItemResponseBody;

interface PriceListItemFormProps {
  defaultValues?: DefaultValues;
  onSubmit: FormSubmitHandler<PriceListItem>;
  items: DefaultValues[];
  vatRate: {
    label: string;
    value: number;
  };
}

export function PriceListItemForm(props: PriceListItemFormProps) {
  const {tenantCurrency} = useTenant();
  const formatCurrency = useCurrencySymbolFormatter();

  const isCreating = isNil(props.defaultValues);

  const defaultValues = areDefaultValuesFromApi(props.defaultValues)
    ? {
        name: props.defaultValues.name,
        from: props.defaultValues?.range.from,
        includedKilometers: props.defaultValues?.price.includedKilometers,
        period: props.defaultValues?.range.period,
        rate: props.defaultValues?.price.rate
          ? {
              withVat: getPriceWithVat(props.defaultValues.price.rate, props.vatRate.value),
              withoutVat: parseFloat(props.defaultValues.price.rate) || 0,
            }
          : undefined,
        ratePerKilometer: props.defaultValues?.price.ratePerKilometer
          ? {
              withVat: getPriceWithVat(
                props.defaultValues.price.ratePerKilometer,
                props.vatRate.value
              ),
              withoutVat: parseFloat(props.defaultValues.price.ratePerKilometer) || 0,
            }
          : undefined,
        to: props.defaultValues?.range.to,
      }
    : props.defaultValues;

  return (
    <Form<PriceListItem>
      defaultValues={defaultValues}
      onSubmit={props.onSubmit}
      experimentalZodSchema={schema}
    >
      {(control, formApi) => (
        <VStack spacing={4} width="100%">
          <Grid columns={2}>
            <GridItem span={2}>
              <FormField
                control={control}
                name="name"
                type="text"
                label={i18n.t('general.labels.name')}
                isRequired
              />
            </GridItem>

            <FormField
              control={control}
              name="from"
              type="number"
              label={i18n.t('entity.rental.labels.from')}
              isStepperVisible
              minStepperValue={0}
              isRequired
            />

            <FormField
              control={control}
              name="to"
              type="number"
              label={i18n.t('entity.rental.labels.to')}
              isStepperVisible
              minStepperValue={0}
              isRequired
            />

            <GridItem span={2}>
              <RateFormRow
                control={control}
                formApi={formApi}
                name="rate"
                label={i18n.t('entity.rental.labels.rate')}
                vatRate={props.vatRate}
              />
            </GridItem>

            <GridItem span={2}>
              <RateFormRow
                control={control}
                formApi={formApi}
                name="ratePerKilometer"
                label={`${formatCurrency(tenantCurrency)}${i18n.t('entity.rental.labels.ratePerKilometer')}`}
                vatRate={props.vatRate}
              />
            </GridItem>

            <FormField
              control={control}
              name="includedKilometers"
              type="number"
              suffix="Km"
              label={i18n.t('entity.rental.labels.includedKilometers')}
              isRequired
            />

            <FormField
              control={control}
              name="period"
              type="choice"
              menuInPortal
              label={i18n.t('entity.rental.labels.period')}
              options={[
                {value: 'day', label: i18n.t('entity.rental.labels.day')},
                {value: 'month', label: i18n.t('entity.rental.labels.month')},
              ]}
              isRequired
            />
          </Grid>
          <HStack justify="flex-end" spacing={2} minWidth="100%">
            <Button
              variant="secondary"
              onClick={closeCurrentDialog}
              title={i18n.t('general.actions.discard')}
            />
            <FormButton
              control={control}
              title={isCreating ? i18n.t('general.actions.create') : i18n.t('general.actions.save')}
              type="submit"
            />
          </HStack>
        </VStack>
      )}
    </Form>
  );
}

const areDefaultValuesFromApi = (
  values: DefaultValues | Nullish
): values is RentalPriceGroupItemResponseBody =>
  isNotNil((values as RentalPriceGroupItemResponseBody)?.rentalPriceGroupItemId);

const schema = z
  .object({
    from: z.number().min(0),
    to: z.number().min(0),
    rate: z.object({
      withVat: z.number().min(0),
      withoutVat: z.number().min(0),
    }),
    ratePerKilometer: z.object({
      withVat: z.number().min(0),
      withoutVat: z.number().min(0),
    }),
    includedKilometers: z.number().min(0),
    period: z.enum(['day', 'month']),
    name: z.string().nonempty(),
  })
  .refine((data) => data.from <= data.to, {
    path: ['from'],
    message: i18n.t('entity.rental.labels.validationFrom'),
  });
